import React from 'react';
import {
  Box,
  Typography,
  Grid,
  IconButton,
  Tooltip,
  Button,
  Chip,
  Paper,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import SimCardRoundedIcon from '@mui/icons-material/SimCardRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';

function ESIMResults({ esimData, copiedField, handleCopy, parseDataValue, onCheckAnother, onRenewPackage }) {
  const getValidityPeriod = () => {
    const planName = esimData.plan_name || '';
    const daysMatch = planName.match(/(\d+)\s*Days?/i) || planName.match(/\/(\d+)\s*days?/i);
    if (daysMatch) return `${daysMatch[1]} days`;
    if (esimData.validity && esimData.validity !== 'N/A') return esimData.validity;
    return 'N/A';
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'N/A') return 'N/A';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
  };

  const chartData = [
    { name: 'Consumed', value: parseDataValue(esimData?.data_consumed), color: '#5C7CFA' },
    { name: 'Remaining', value: parseDataValue(esimData?.data_remaining), color: '#1F3262' },
  ];

  const InfoField = ({ label, value, copyable = false, copyKey = '' }) => (
    <Box sx={{ mb: 2.5 }}>
      <Typography sx={{ fontSize: '0.82rem', color: '#7B879E', fontWeight: 700, mb: 0.8 }}>{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
        <Typography sx={{ fontSize: '1.02rem', color: '#14213d', fontWeight: 700, lineHeight: 1.3, wordBreak: 'break-word' }}>{value}</Typography>
        {copyable && (
          <Tooltip title={copiedField === copyKey ? 'Copied!' : 'Copy'}>
            <IconButton size="small" onClick={() => handleCopy(value, copyKey)} sx={{ color: '#7888A1' }}>
              <ContentCopyIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );

  const StatCard = ({ icon, label, value, bg = '#EEF2FF' }) => (
    <Paper elevation={0} sx={{ p: 2.4, borderRadius: '18px', border: '1px solid #edf1f7', height: '100%' }}>
      <Box sx={{ width: 44, height: 44, borderRadius: 3, background: bg, display: 'grid', placeItems: 'center', mb: 1.7 }}>{icon}</Box>
      <Typography sx={{ color: '#72809A', fontWeight: 700, fontSize: '1rem' }}>{label}</Typography>
      <Typography sx={{ color: '#111C3A', fontWeight: 800, fontSize: '2rem', lineHeight: 1.2 }}>{value}</Typography>
    </Paper>
  );

  return (
    <Box sx={{ background: '#F6F8FC', minHeight: '100vh', py: 2, px: { xs: 1, sm: 2 } }}>
      <Paper elevation={0} sx={{ maxWidth: 460, mx: 'auto', borderRadius: '22px', border: '1px solid #E7ECF4', overflow: 'hidden', backgroundColor: '#fff' }}>
        <Box sx={{ p: 2.5, pb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ width: 32 }} />
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#111C3A' }}>SIM Status Report</Typography>
            <Box sx={{ width: 32 }} />
          </Box>

          <Box sx={{ mt: 2.2, display: 'flex', gap: 1.2, flexWrap: 'wrap' }}>
            <Chip label={esimData.status} sx={{ background: '#FEF3D6', color: '#CC7A00', fontWeight: 800, borderRadius: '10px' }} />
            <Button variant="outlined" onClick={onCheckAnother} sx={{ borderRadius: '12px', textTransform: 'none', color: '#22314E', borderColor: '#D4DCE8', px: 2.2 }}>Check Another</Button>
            <Button variant="contained" startIcon={<AutorenewIcon />} onClick={onRenewPackage} sx={{ borderRadius: '12px', textTransform: 'none', bgcolor: '#162755', px: 2.2 }}>Renew</Button>
          </Box>
        </Box>

        <Box sx={{ p: 2.5 }}>
              <Paper elevation={0} sx={{ mt: 2, p: 2.2, borderRadius: '18px', border: '1px solid #edf1f7' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.4 }}><SimCardRoundedIcon sx={{ color: '#4E6BFF' }} /><Typography sx={{ fontWeight: 800, color: '#111C3A', fontSize: '1.95rem' }}>SIM Information</Typography></Box>
                <InfoField label="ICCID" value={esimData.iccid} copyable copyKey="iccid" />
                <Box sx={{ mb: 2.5 }}>
                  <Typography sx={{ fontSize: '0.82rem', color: '#7B879E', fontWeight: 700, mb: 0.8 }}>START DATE</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <CalendarMonthRoundedIcon sx={{ color: '#1FBF9D', fontSize: 20 }} />
                    <Typography sx={{ fontSize: '1.02rem', color: '#14213d', fontWeight: 700, lineHeight: 1.3, wordBreak: 'break-word' }}>
                      {formatDate(esimData.start_date || esimData.purchase_date)}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ mb: 2.5 }}>
                  <Typography sx={{ fontSize: '0.82rem', color: '#7B879E', fontWeight: 700, mb: 0.8 }}>END DATE</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <CalendarMonthRoundedIcon sx={{ color: '#E4547C', fontSize: 20 }} />
                    <Typography sx={{ fontSize: '1.02rem', color: '#14213d', fontWeight: 700, lineHeight: 1.3, wordBreak: 'break-word' }}>
                      {formatDate(esimData.end_date)}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ mt: 0.5, p: 1.6, borderRadius: '14px', border: '1px solid #E8EEF8', backgroundColor: '#F8FAFF' }}>
                  <Typography sx={{ fontSize: '0.82rem', color: '#7B879E', fontWeight: 700, mb: 1 }}>PLAN DESCRIPTION</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
                    <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: '#1E4BD9', color: '#FFDA44', display: 'grid', placeItems: 'center', fontSize: '0.9rem', boxShadow: '0 2px 8px rgba(30, 75, 217, 0.25)' }}>
                      🇪🇺
                    </Box>
                    <Typography sx={{ fontSize: '1.02rem', color: '#14213d', fontWeight: 700, lineHeight: 1.35 }}>{esimData.plan_name}</Typography>
                  </Box>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ mt: 2, p: 2.2, borderRadius: '18px', border: '1px solid #edf1f7' }}>
                <Typography sx={{ fontWeight: 800, color: '#111C3A', mb: 1.8, fontSize: '1.8rem' }}>Usage Overview</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Box sx={{ width: 260, height: 260, position: 'relative' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <defs>
                          <linearGradient id="consumedGradient" x1="0" y1="0" x2="1" y2="1">
                            <stop offset="0%" stopColor="#7DA0FF" />
                            <stop offset="100%" stopColor="#476BFF" />
                          </linearGradient>
                          <linearGradient id="remainingGradient" x1="0" y1="0" x2="1" y2="1">
                            <stop offset="0%" stopColor="#2D437A" />
                            <stop offset="100%" stopColor="#162855" />
                          </linearGradient>
                        </defs>
                        <Pie
                          data={chartData}
                          dataKey="value"
                          cx="50%"
                          cy="50%"
                          innerRadius={78}
                          outerRadius={108}
                          stroke="#FFFFFF"
                          strokeWidth={4}
                          paddingAngle={1}
                        >
                          <Cell fill="url(#consumedGradient)" />
                          <Cell fill="url(#remainingGradient)" />
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                    <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', textAlign: 'center', pointerEvents: 'none' }}>
                      <Box sx={{ width: 130, height: 130, borderRadius: '50%', backgroundColor: '#fff', boxShadow: '0 8px 25px rgba(22, 40, 85, 0.08)', display: 'grid', placeItems: 'center' }}>
                        <Box>
                          <Typography sx={{ fontWeight: 800, color: '#111C3A', fontSize: '2rem', lineHeight: 1 }}>{parseDataValue(esimData?.data_remaining).toFixed(1)}</Typography>
                          <Typography sx={{ color: '#66758F', fontWeight: 700 }}>GB left</Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ display: 'grid', gap: 1.2, mt: 1.6 }}>
                  {chartData.map((item) => <Box key={item.name} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#F8FAFF', border: '1px solid #ECF0F8', borderRadius: '12px', px: 1.4, py: 1 }}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ width: 12, height: 12, borderRadius: '50%', background: item.name === 'Consumed' ? 'linear-gradient(135deg, #7DA0FF 0%, #476BFF 100%)' : 'linear-gradient(135deg, #2D437A 0%, #162855 100%)' }} /><Typography sx={{ color: '#596781', fontWeight: 700 }}>{item.name}</Typography></Box><Typography sx={{ color: '#111C3A', fontWeight: 800 }}>{item.value.toFixed(2)} GB</Typography></Box>)}
                </Box>
                <Paper elevation={0} sx={{ mt: 2, p: 1.8, borderRadius: '14px', bgcolor: '#F4F7FF' }}><Box sx={{ display: 'flex', gap: 1, alignItems: 'start' }}><InfoOutlinedIcon sx={{ color: '#3764FF', mt: 0.2 }} /><Typography sx={{ color: '#2E53BE', fontWeight: 700 }}>You have {parseDataValue(esimData?.data_remaining).toFixed(2)} GB remaining. Enjoy your trip!</Typography></Box></Paper>
              </Paper>
          <Typography sx={{ color: '#7D8BA5', textAlign: 'center', fontWeight: 600, mt: 2 }}>Last updated: Just now</Typography>
          <Box sx={{ position: 'fixed', right: 16, bottom: 20, zIndex: 1300 }}>
            <Button
              component="a"
              href="https://wa.me/905326326622"
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              startIcon={<WhatsAppIcon />}
              sx={{
                borderRadius: '999px',
                textTransform: 'none',
                bgcolor: '#25D366',
                color: '#fff',
                px: 2,
                py: 1,
                boxShadow: '0 8px 18px rgba(37, 211, 102, 0.35)',
                '&:hover': { bgcolor: '#1ebe5d' },
              }}
            >
              Yardım
            </Button>
          </Box>

        </Box>
      </Paper>
    </Box>
  );
}

export default ESIMResults;
